import React, { useState, useEffect, useRef } from 'react';
import type { AttachmentItem, CandidateRecordItem, RecordItem, UploadPolicy } from '../../types';
import { ApiClient } from '../../api/client';
import { ArchiveDialog } from './ArchiveDialog';
import { useQuota } from '../../hooks/useQuota';
import { useAuth } from '../../context/AuthContext';
import { validateSingleFile, checkStorageQuota, formatBytes as formatBytesUtil } from '../../utils/uploadValidator';
import {
  Paperclip,
  X,
  UploadCloud,
  FileText,
  FileSpreadsheet,
  FileImage,
  File,
  Download,
  Trash2,
  Edit2,
  Check,
  RefreshCw,
  Copy,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Plus,
  Eye,
  HardDrive,
  FileInput,
  Archive,
} from 'lucide-react';
import { UniversalFilePreviewModal } from '../common/UniversalFilePreviewModal';
import { isImportable } from '../../utils/documentImport';

export function getFileIcon(filename: string, mime?: string) {
  const ext = (filename.split('.').pop() || '').toLowerCase();
  const isHtml = ['html', 'htm'].includes(ext) || mime?.toLowerCase().includes('text/html');
  if (isHtml) return <AlertTriangle className="w-4 h-4 text-amber-500" />;
  if (['pdf'].includes(ext)) return <FileText className="w-4 h-4 text-red-500" />;
  if (['xlsx', 'xls', 'csv'].includes(ext)) return <FileSpreadsheet className="w-4 h-4 text-emerald-500" />;
  if (['doc', 'docx', 'odt'].includes(ext)) return <FileText className="w-4 h-4 text-blue-500" />;
  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'].includes(ext)) return <FileImage className="w-4 h-4 text-purple-500" />;
  return <File className="w-4 h-4 text-slate-500" />;
}

export function formatFileSize(bytes?: number): string {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

// ═════════════════════════════════════════════════════════════════════
// 1. HOVER POPOVER BADGE KOMPONENS
// ═════════════════════════════════════════════════════════════════════
export const AttachmentHoverBadge: React.FC<{
  count: number;
  recordId: number;
  onClick: () => void;
}> = ({ count, recordId, onClick }) => {
  if (!count || count <= 0) return null;

  const [isHovered, setIsHovered] = useState(false);
  const [previewFiles, setPreviewFiles] = useState<AttachmentItem[] | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [placement, setPlacement] = useState<'top' | 'bottom'>('top');
  const badgeRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<any>(null);

  const handleMouseEnter = () => {
    if (count === 0) return;
    if (badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      if (rect.top < 220) {
        setPlacement('bottom');
      } else {
        setPlacement('top');
      }
    }
    timerRef.current = setTimeout(async () => {
      setIsHovered(true);
      if (!previewFiles) {
        setLoadingPreview(true);
        try {
          const res = await ApiClient.getRecordAttachments(recordId);
          if (res.ok && res.data) {
            setPreviewFiles(res.data);
          }
        } catch (e) {
          console.error(e);
        } finally {
          setLoadingPreview(false);
        }
      }
    }, 120);
  };

  const handleMouseLeave = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setIsHovered(false);
  };

  return (
    <div
      ref={badgeRef}
      className={`relative inline-block ${isHovered ? 'z-50' : 'z-10'}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:border-blue-400 font-mono text-[11px] border border-blue-200 dark:border-blue-800 font-medium cursor-pointer transition shadow-2xs group"
        title={count === 0 ? 'Kattintson új fájl csatolásához' : undefined}
      >
        <Paperclip className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform" />
        <span>{count} db</span>
      </button>

      {/* Lebegő Előnézet Popover */}
      {isHovered && count > 0 && (
        <div
          className={`absolute z-50 left-0 ${
            placement === 'top' ? 'bottom-full mb-2.5' : 'top-full mt-2.5'
          } w-72 sm:w-80 p-3 bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 text-left text-xs pointer-events-none animate-in fade-in zoom-in-95 duration-150`}
        >
          <div className="flex items-center justify-between font-bold text-[11px] text-slate-700 dark:text-slate-200 pb-1.5 border-b border-slate-100 dark:border-slate-800 mb-2">
            <span className="flex items-center gap-1">
              <Paperclip className="w-3.5 h-3.5 text-blue-500" /> Csatolt fájlok ({count} db)
            </span>
            <span className="text-[9px] text-slate-400 font-normal">Kattintson a kezeléshez</span>
          </div>

          {loadingPreview ? (
            <div className="py-2.5 text-center text-slate-400 flex items-center justify-center gap-1.5 text-[11px]">
              <RefreshCw className="w-3.5 h-3.5 animate-spin text-blue-500" />
              <span>Fájlok betöltése...</span>
            </div>
          ) : previewFiles && previewFiles.length > 0 ? (
            <ul className="space-y-1.5 max-h-48 overflow-hidden">
              {previewFiles.slice(0, 5).map((f) => (
                <li key={f.id} className="flex items-center gap-2 text-slate-700 dark:text-slate-300 truncate">
                  {getFileIcon(f.filename)}
                  <span className="truncate flex-1 font-medium text-[11px]">{f.filename}</span>
                  <span className="text-[10px] text-slate-400 shrink-0 font-mono">
                    {formatFileSize(f.size)}
                  </span>
                </li>
              ))}
              {previewFiles.length > 5 && (
                <li className="text-[10px] text-blue-600 dark:text-blue-400 font-medium text-center pt-1">
                  + további {previewFiles.length - 5} fájl kezelése...
                </li>
              )}
            </ul>
          ) : (
            <p className="text-[10px] text-slate-400 italic">Nincs előnézeti adat</p>
          )}

          {/* Kis nyíl mutató */}
          {placement === 'top' ? (
            <div className="absolute left-6 top-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-white dark:border-t-slate-700 drop-shadow-xs" />
          ) : (
            <div className="absolute left-6 bottom-full w-0 h-0 border-x-4 border-x-transparent border-b-4 border-b-white dark:border-b-slate-700 drop-shadow-xs" />
          )}
        </div>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════
// 1.5. BEJEGYZÉS SZERKESZTŐBE ÁGYAZOTT CSATOLMÁNY-KEZELŐ (ACCORDION)
// ═════════════════════════════════════════════════════════════════════
export const RecordAttachmentsSection: React.FC<{
  recordId: number;
  tenantSlug?: string;
  pageTitle?: string;
  onAttachmentsChanged?: (newCount: number) => void;
  onOpenAdvancedModal?: () => void;
}> = ({ recordId, tenantSlug, onAttachmentsChanged, onOpenAdvancedModal }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewFile, setPreviewFile] = useState<{ url: string; name: string; mime?: string; id?: number } | null>(null);
  const [archiveAtt, setArchiveAtt] = useState<{ id: number; filename: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadAttachments = async () => {
    if (!recordId) return;
    setIsLoading(true);
    try {
      const res = await ApiClient.getRecordAttachments(recordId);
      if (res.ok && res.data) {
        setAttachments(res.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAttachments();
  }, [recordId]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (const f of Array.from(files)) {
      const v = validateSingleFile(f);
      if (!v.valid) {
        alert(v.error);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
    }

    setIsUploading(true);
    setUploadStatus(`Feltöltés folyamatban (${files.length} db fájl)...`);

    try {
      const res = await ApiClient.uploadRecordAttachments(recordId, Array.from(files));
      if (res.ok) {
        const listRes = await ApiClient.getRecordAttachments(recordId);
        if (listRes.ok && listRes.data) {
          setAttachments(listRes.data);
          onAttachmentsChanged?.(listRes.data.length);
        }
      } else {
        alert(res.error || 'Hiba történt a feltöltés során');
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      alert(err.message || 'Hiba történt a feltöltés során');
    } finally {
      setIsUploading(false);
      setUploadStatus('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (attId: number, filename: string) => {
    if (!confirm(`Biztosan törölni szeretné a(z) "${filename}" mellékletet?`)) return;
    try {
      const res = await ApiClient.deleteAttachment(attId);
      if (res.ok) {
        const next = attachments.filter(a => a.id !== attId);
        setAttachments(next);
        onAttachmentsChanged?.(next.length);
        setNotice((res as any).archived ? `„${filename}” az Archívumba került (automatikus archiválás).` : null);
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-2xs">
      <ArchiveDialog isOpen={Boolean(archiveAtt)} kind="attachment" ids={archiveAtt ? [archiveAtt.id] : []} label={archiveAtt?.filename}
        onClose={() => setArchiveAtt(null)}
        onDone={() => {
          const next = attachments.filter((a) => a.id !== archiveAtt?.id);
          setAttachments(next); onAttachmentsChanged?.(next.length);
          setNotice(`„${archiveAtt?.filename}” az Archívumba került.`);
        }} />
      {notice && <p role="status" className="px-3 pt-2 text-[11px] text-emerald-700 dark:text-emerald-400">{notice}</p>}
      {/* Harmonika Fejléc */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="px-4 py-2.5 flex items-center justify-between gap-2 cursor-pointer select-none bg-slate-50/80 dark:bg-slate-800/50 hover:bg-slate-100/70 dark:hover:bg-slate-800/80 transition"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Paperclip className="w-4 h-4 text-blue-600 dark:text-cyan-400 shrink-0" />
          <span className="font-bold text-xs text-slate-800 dark:text-slate-200">
            Csatolt fájlok és mellékletek
          </span>
          <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full font-bold ${
            attachments.length > 0
              ? 'bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-cyan-300'
              : 'bg-slate-200 dark:bg-slate-800 text-slate-500'
          }`}>
            {attachments.length} db
          </span>
        </div>

        <div className="flex items-center gap-2">
          {isLoading && <RefreshCw className="w-3.5 h-3.5 animate-spin text-blue-500" />}
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </div>

      {/* Kinyitott tartalom */}
      {isExpanded && (
        <div className="p-3 space-y-3 border-t border-slate-200 dark:border-slate-800 text-xs">
          {/* Feltöltési sáv */}
          <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-lg border border-dashed border-blue-300 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-950/20">
            <div className="flex items-center gap-2 min-w-0">
              <UploadCloud className="w-4 h-4 text-blue-600 dark:text-cyan-400 shrink-0" />
              <div className="text-[11px] leading-tight">
                <span className="font-semibold text-slate-800 dark:text-slate-200 block">
                  Új fájl hozzáadása a bejegyzéshez
                </span>
                <span className="text-[10px] text-slate-400">
                  PDF, XLSX, DOCX, képek, archívumok
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                disabled={isUploading}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-medium text-xs flex items-center gap-1.5 transition cursor-pointer shadow-xs"
              >
                {isUploading ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Feltöltés...</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-3.5 h-3.5" />
                    <span>Fájl feltöltése</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Státusz üzenet feltöltéskor */}
          {uploadStatus && (
            <div className="p-2 rounded-lg bg-blue-100/60 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300 text-[11px] font-medium flex items-center gap-2">
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              <span>{uploadStatus}</span>
            </div>
          )}

          {/* Csatolt fájlok listája */}
          {attachments.length === 0 ? (
            <div className="text-center py-3 text-slate-400 italic text-[11px]">
              Még nincs csatolt fájl ehhez a bejegyzéshez. Kattintson a fenti gombra új fájl feltöltéséhez.
            </div>
          ) : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
              {attachments.map((att) => {
                const token = ApiClient.getToken();
                const rawUrl = att.r2_key
                  ? `/api/files/${encodeURIComponent(att.r2_key)}`
                  : `/api/public/${tenantSlug}/attachments/${att.id}`;
                const downloadUrl = token && rawUrl.startsWith('/api/files/')
                  ? `${rawUrl}?token=${encodeURIComponent(token)}`
                  : rawUrl;

                const fileExt = (att.filename.split('.').pop() || '').toLowerCase();
                const isHtmlSuspicious = att.mime_type?.includes('text/html') || ['html', 'htm'].includes(fileExt);

                return (
                  <div
                    key={att.id}
                    className={`flex items-center justify-between gap-2 p-2 rounded-lg border transition ${
                      isHtmlSuspicious
                        ? 'bg-amber-50/50 dark:bg-amber-950/20 border-amber-300/80 dark:border-amber-800/60 hover:border-amber-400'
                        : 'bg-slate-50 dark:bg-slate-800/60 border-slate-200/80 dark:border-slate-800 hover:border-blue-300 dark:hover:border-blue-800'
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div className={`p-1.5 rounded shrink-0 ${isHtmlSuspicious ? 'bg-amber-100 dark:bg-amber-950/60 text-amber-600' : 'bg-white dark:bg-slate-900'}`}>
                        {getFileIcon(att.filename, att.mime_type)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="font-semibold text-slate-800 dark:text-slate-200 text-xs truncate" title={att.filename}>
                            {att.filename}
                          </p>
                          {isHtmlSuspicious && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded text-[9px] font-bold bg-amber-100 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700 shrink-0"
                              title="Ez a fájl mentett weboldalt / bejelentkező felületet tartalmaz PDF dokumentum helyett!"
                            >
                              <AlertTriangle className="w-2.5 h-2.5 text-amber-600 dark:text-amber-400 shrink-0" />
                              Mentett weboldal (HTML)
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-400 font-mono">
                          {formatFileSize(att.size)} · {att.uploaded_at ? new Date(att.uploaded_at).toLocaleDateString('hu-HU') : ''}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => setPreviewFile({ url: downloadUrl, name: att.filename, mime: att.mime_type, id: att.id })}
                        className="p-1.5 text-slate-500 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-white dark:hover:bg-slate-900 rounded transition cursor-pointer"
                        title="Előnézet megnyitása"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      <a
                        href={downloadUrl}
                        target="_blank"
                        rel="noreferrer"
                        download={att.filename}
                        className="p-1.5 text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-white dark:hover:bg-slate-900 rounded transition cursor-pointer"
                        title="Letöltés"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </a>
                      <button
                        type="button"
                        onClick={() => setArchiveAtt({ id: att.id, filename: att.filename })}
                        className="p-1.5 text-slate-400 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-white dark:hover:bg-slate-900 rounded transition cursor-pointer"
                        title="Archiválás (a jogszabályi megőrzési idő kijelzésével)"
                        aria-label={`${att.filename} archiválása`}
                      >
                        <Archive className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(att.id, att.filename)}
                        className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-white dark:hover:bg-slate-900 rounded transition cursor-pointer"
                        title="Fájl törlése"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* További haladó műveletek (átnevezés, másolás másik ponthoz) */}
          {attachments.length > 0 && onOpenAdvancedModal && (
            <div className="pt-2 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between text-[11px]">
              <span className="text-slate-400">
                Átnevezéshez vagy másik ponthoz társításhoz:
              </span>
              <button
                type="button"
                onClick={onOpenAdvancedModal}
                className="font-semibold text-blue-600 dark:text-cyan-400 hover:underline flex items-center gap-1 cursor-pointer shrink-0"
              >
                <FolderOpen className="w-3 h-3" />
                <span>Részletes fájlkezelő megnyitása</span>
              </button>
            </div>
          )}
        </div>
      )}

      {previewFile && (
        <UniversalFilePreviewModal
          isOpen={Boolean(previewFile)}
          onClose={() => setPreviewFile(null)}
          fileName={previewFile.name}
          fileUrl={previewFile.url}
          mimeType={previewFile.mime}
          onReplace={onOpenAdvancedModal}
        />
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════
// 2. RÉSZLETES MELLÉKLETKEZELŐ MODAL
// ═════════════════════════════════════════════════════════════════════
interface AttachmentManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  record: RecordItem | null;
  pageTitle?: string;
  tenantSlug?: string;
  onAttachmentsChanged?: () => void;
  /** „Betöltés tartalomként”: meglévő melléklet vagy új fájl → a pont leírása */
  onImportAsContent?: (src: { kind: 'existing'; attachment: AttachmentItem } | { kind: 'new'; file: File }) => void;
  /** változásakor a lista újratöltődik (pl. tartalomként betöltés után) */
  reloadKey?: number;
}

export const AttachmentManagerModal: React.FC<AttachmentManagerModalProps> = ({
  isOpen,
  onClose,
  record,
  pageTitle,
  tenantSlug = 'system',
  onAttachmentsChanged,
  onImportAsContent,
  reloadKey,
}) => {
  const contentInputRef = React.useRef<HTMLInputElement>(null);
  const { tenant } = useAuth();
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [archiveAtt, setArchiveAtt] = useState<AttachmentItem | null>(null);
  // a ténylegesen érvényes kvóta (saját vagy globális) és a foglalt tárhely a Lomtárral és az Archívummal együtt

  // Tárhely és kvóta állapotok
  const [currentStorageBytes, setCurrentStorageBytes] = useState<number>(0);
  const { limitMb: quotaLimitMb, usedBytes: quotaUsedBytes, reload: reloadQuota } = useQuota(isOpen);
  const storageLimitMb = quotaLimitMb ?? tenant?.max_storage_mb ?? 500;
  const storageUsed = quotaUsedBytes ?? currentStorageBytes;
  const storagePct = storageLimitMb > 0 ? Math.min(100, Math.round((storageUsed / (storageLimitMb * 1024 * 1024)) * 100)) : 0;
  const [uploadPolicy, setUploadPolicy] = useState<UploadPolicy | null>(null);

  // Rename state
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [newFilename, setNewFilename] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  // Assign / Move state
  const [assigningId, setAssigningId] = useState<number | null>(null);
  const [candidateRecords, setCandidateRecords] = useState<CandidateRecordItem[]>([]);
  const [targetRecordId, setTargetRecordId] = useState<string>('');
  const [assignMode, setAssignMode] = useState<'copy' | 'move'>('copy');
  const [isAssigning, setIsAssigning] = useState(false);

  // Drag & drop state
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewFile, setPreviewFile] = useState<{ url: string; name: string; mime?: string; id?: number } | null>(null);

  // Replace state
  const [replacingAttId, setReplacingAttId] = useState<number | null>(null);
  const [isReplacing, setIsReplacing] = useState(false);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const pendingReplaceIdRef = useRef<number | null>(null);

  const loadQuotaAndPolicy = async () => {
    try {
      const [overviewRes, policyRes] = await Promise.all([
        ApiClient.getAttachmentsOverview(record?.tenant_id || tenant?.id),
        ApiClient.getUploadPolicy()
      ]);
      // B6: a /api/attachments/overview a méretet a `data` borítékban adja
      // ({ ok, data: { total_size } }) – a gyökérszintű olvasás mindig 0 B-t mutatott.
      const overview: any = (overviewRes as any).data ?? overviewRes;
      if (overviewRes.ok && overview?.total_size !== undefined) {
        setCurrentStorageBytes(Number(overview.total_size) || 0);
      }
      reloadQuota();
      if (policyRes.ok && policyRes.data) {
        setUploadPolicy(policyRes.data);
      }
    } catch {}
  };

  const handleTriggerReplace = (attId: number) => {
    pendingReplaceIdRef.current = attId;
    if (replaceInputRef.current) {
      replaceInputRef.current.value = '';
      replaceInputRef.current.click();
    }
  };

  const handleExecuteReplace = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const attId = pendingReplaceIdRef.current;
    if (!file || !attId) return;

    setUploadError(null);
    setSuccessMsg(null);

    // 1. Tiltólista & méret ellenőrzés
    const v = validateSingleFile(file, uploadPolicy);
    if (!v.valid) {
      setUploadError(v.error || 'A fájl nem felel meg a biztonsági szabályzatnak.');
      return;
    }

    // 2. Kvóta ellenőrzés méretkülönbözettel
    const existingAtt = attachments.find(a => a.id === attId);
    const sizeDelta = Math.max(0, file.size - (existingAtt?.size || 0));
    const maxMb = storageLimitMb;
    const q = checkStorageQuota(storageUsed, sizeDelta, maxMb);
    if (q.isOverQuota) {
      setUploadError(q.error || 'A cserével a szervezet elérné a tárhelykorlátot!');
      return;
    }

    setIsReplacing(true);
    setReplacingAttId(attId);
    try {
      const res = await ApiClient.replaceAttachment(attId, file);
      if (res.ok) {
        setSuccessMsg(`✅ Fájl sikeresen lecserélve erre: „${file.name}”!`);
        await loadAttachments();
        await loadQuotaAndPolicy();
        onAttachmentsChanged?.();
      } else {
        setUploadError(res.error || 'Hiba történt a fájl cseréje során.');
      }
    } catch (err: any) {
      setUploadError(err.message || 'Hálózati hiba');
    } finally {
      setIsReplacing(false);
      setReplacingAttId(null);
      pendingReplaceIdRef.current = null;
    }
  };

  const loadAttachments = async () => {
    if (!record) return;
    setLoading(true);
    try {
      const res = await ApiClient.getRecordAttachments(record.id);
      if (res.ok && res.data) {
        setAttachments(res.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCandidates = async () => {
    try {
      const res = await ApiClient.getCandidateRecords();
      if (res.ok && res.data) {
        // Exclude current record
        setCandidateRecords(res.data.filter(c => c.id !== record?.id));
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (isOpen && record) {
      loadAttachments();
      loadCandidates();
      loadQuotaAndPolicy();
      setRenamingId(null);
      setAssigningId(null);
      setUploadError(null);
      setSuccessMsg(null);
    }
  }, [isOpen, record?.id, reloadKey]);

  if (!isOpen || !record) return null;

  const pointLabel = record.parsedData?.pont || `${record.sort_order}. pont`;
  const pointTitle = record.parsedData?.megnevezes || record.parsedData?.cim || record.parsedData?.nev || 'Közzétételi pont';
  // Upload handler
  const handleUploadFiles = async (files: File[]) => {
    if (!files || files.length === 0) return;
    setUploadError(null);
    setSuccessMsg(null);

    // 1. Duplikáció ellenőrzés a most kiválasztott fájlok között
    const batchNames = new Set<string>();
    for (const f of files) {
      const lower = f.name.toLowerCase();
      if (batchNames.has(lower)) {
        setUploadError(`⚠️ A kiválasztott fájlok között a(z) „${f.name}” többször is szerepel! Kérjük, távolítsa el a duplikátumot.`);
        return;
      }
      batchNames.add(lower);
    }

    // 2. Duplikáció ellenőrzés a rekordhoz már csatolt meglévő fájlokkal szemben
    const existingNames = new Set(attachments.map(a => a.filename.toLowerCase()));
    for (const f of files) {
      if (existingNames.has(f.name.toLowerCase())) {
        setUploadError(`⚠️ A(z) „${f.name}” nevű fájl már csatolva van ehhez a bejegyzéshez! Ugyanaz a fájl nem tölthető fel kétszer. Amennyiben frissíteni vagy módosítani szeretné, kérjük, használja a listában a fájl melletti Csere (🔄) gombot!`);
        return;
      }
    }

    // 3. Tiltólista & méret ellenőrzés minden fájlra
    for (const f of files) {
      const v = validateSingleFile(f, uploadPolicy);
      if (!v.valid) {
        setUploadError(v.error || 'A fájl nem felel meg a biztonsági szabályzatnak.');
        return;
      }
    }

    // 4. Kvóta ellenőrzés
    const incomingBytes = files.reduce((sum, f) => sum + (f.size || 0), 0);
    const maxMb = storageLimitMb;
    const q = checkStorageQuota(storageUsed, incomingBytes, maxMb);
    if (q.isOverQuota) {
      setUploadError(q.error || 'A feltöltéssel a szervezet elérné a tárhelykorlátot!');
      return;
    }

    setIsUploading(true);
    try {
      const res = await ApiClient.uploadRecordAttachments(record.id, files);
      if (res.ok) {
        setSuccessMsg(`✅ ${files.length} db fájl sikeresen feltöltve!`);
        await loadAttachments();
        await loadQuotaAndPolicy();
        onAttachmentsChanged?.();
      } else {
        setUploadError(res.error || 'Hiba történt a feltöltés során');
      }
    } catch (e: any) {
      setUploadError(e.message || 'Hálózati hiba történt');
    } finally {
      setIsUploading(false);
    }
  };

  // Delete handler
  const handleDelete = async (att: AttachmentItem) => {
    if (!confirm(`Biztosan törölni kívánja a(z) "${att.filename}" mellékletet? A melléklet a Lomtárba kerül, ahonnan 30 napig visszaállítható.`)) {
      return;
    }
    try {
      const res = await ApiClient.deleteAttachment(att.id);
      if (res.ok) {
        setAttachments(prev => prev.filter(a => a.id !== att.id));
        setSuccessMsg((res as any).archived
          ? `A(z) "${att.filename}" az Archívumba került (automatikus archiválás).`
          : `🗑️ A(z) "${att.filename}" sikeresen törölve.`);
        await loadQuotaAndPolicy();
        onAttachmentsChanged?.();
      } else {
        alert(res.error || 'Nem sikerült törölni a fájlt');
      }
    } catch (e: any) {
      alert('Hiba: ' + e.message);
    }
  };

  // Rename handler
  const handleStartRename = (att: AttachmentItem) => {
    setRenamingId(att.id);
    setNewFilename(att.filename);
  };

  const handleSaveRename = async (attId: number) => {
    const trimmed = newFilename.trim();
    if (!trimmed) return;
    const lower = trimmed.toLowerCase();
    const collision = attachments.some(a => a.id !== attId && a.filename.toLowerCase() === lower);
    if (collision) {
      alert(`⚠️ Már létezik egy „${trimmed}” nevű melléklet ennél a bejegyzésnél! Kérjük, válasszon egyedi nevet.`);
      return;
    }
    setIsRenaming(true);
    try {
      const res = await ApiClient.renameAttachment(attId, trimmed);
      if (res.ok) {
        setAttachments(prev =>
          prev.map(a => (a.id === attId ? { ...a, filename: trimmed } : a))
        );
        setRenamingId(null);
        setSuccessMsg('Fájl sikeresen átnevezve!');
        onAttachmentsChanged?.();
      } else {
        alert(res.error || 'Hiba az átnevezéskor');
      }
    } catch (e: any) {
      alert('Hiba: ' + e.message);
    } finally {
      setIsRenaming(false);
    }
  };

  // Assign / Move handler
  const handleExecuteAssign = async (attId: number) => {
    if (!targetRecordId) return;
    if (Number(targetRecordId) === record.id) {
      alert('⚠️ A fájl már ehhez a közzétételi ponthoz tartozik! Válasszon másik célpontot.');
      return;
    }
    setIsAssigning(true);
    try {
      const res = await ApiClient.assignAttachment(attId, Number(targetRecordId), assignMode);
      if (res.ok) {
        setSuccessMsg(
          assignMode === 'move'
            ? 'Fájl sikeresen átmozgatva a kiválasztott ponthoz!'
            : 'Fájl sikeresen hozzárendelve (másolva) a kiválasztott ponthoz!'
        );
        setAssigningId(null);
        setTargetRecordId('');
        await loadAttachments();
        onAttachmentsChanged?.();
      } else {
        alert(res.error || 'Hiba a művelet végrehajtásakor');
      }
    } catch (e: any) {
      alert('Hiba: ' + e.message);
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200 overflow-y-auto">
      <ArchiveDialog isOpen={Boolean(archiveAtt)} kind="attachment" ids={archiveAtt ? [archiveAtt.id] : []} label={archiveAtt?.filename}
        onClose={() => setArchiveAtt(null)}
        onDone={async () => {
          const a = archiveAtt;
          setAttachments((prev) => prev.filter((x) => x.id !== a?.id));
          setSuccessMsg(`A(z) "${a?.filename}" az Archívumba került.`);
          await loadQuotaAndPolicy();
          onAttachmentsChanged?.();
        }} />
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-2xl shadow-2xl my-auto overflow-hidden flex flex-col max-h-[90vh]">
        {/* Fejléc */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/70 dark:bg-slate-950/40">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold">
              <Paperclip className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-base text-slate-900 dark:text-white">
                  Mellékletek Kezelése
                </span>
                <span className="px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 font-semibold text-xs border border-blue-200 dark:border-blue-800">
                  {attachments.length} db fájl
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-lg">
                [{pageTitle || 'Oldal'}] {pointLabel} – {pointTitle}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-white rounded-lg transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Test / Fájllista és Feltöltő */}
        <input
          ref={replaceInputRef}
          type="file"
          className="hidden"
          onChange={handleExecuteReplace}
        />
        <div className="p-6 overflow-y-auto space-y-5 flex-1">
          {/* Értesítések */}
          {successMsg && (
            <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-xl text-xs text-emerald-800 dark:text-emerald-300 flex items-center gap-2 animate-in fade-in">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>{successMsg}</span>
            </div>
          )}
          {uploadError && (
            <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-xl text-xs text-red-800 dark:text-red-300 flex items-center gap-2 animate-in fade-in">
              <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
              <span>{uploadError}</span>
            </div>
          )}

          {/* Szervezeti Tárhely Kvóta Kijelző */}
          {tenant && (
            <div className="bg-slate-50 dark:bg-slate-950/40 p-3 rounded-xl border border-slate-200 dark:border-slate-800 space-y-1.5">
              <div className="flex items-center justify-between text-xs font-semibold text-slate-700 dark:text-slate-300">
                <div className="flex items-center gap-1.5">
                  <HardDrive className="w-3.5 h-3.5 text-slate-500" />
                  <span>Szervezeti tárhely kvóta:</span>
                </div>
                <span className="font-mono text-slate-600 dark:text-slate-400">
                  {formatBytesUtil(storageUsed)} / {storageLimitMb > 0 ? `${storageLimitMb} MB` : 'korlátlan'} ({storagePct}%)
                </span>
              </div>
              <div className="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                <div
                  style={{ width: `${storagePct}%` }}
                  className={`h-full transition-all duration-300 ${storagePct > 90 ? 'bg-amber-500' : 'bg-blue-600'}`}
                />
              </div>
              <p className="text-[10px] text-slate-500 dark:text-slate-400">A Lomtárban és az Archívumban lévő fájlok is beleszámítanak.</p>
            </div>
          )}

          {/* Feltöltési dropzone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragOver(false);
              if (e.dataTransfer.files) handleUploadFiles(Array.from(e.dataTransfer.files));
            }}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-5 text-center transition cursor-pointer flex flex-col items-center justify-center gap-2 ${
              isDragOver
                ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-950/20'
                : 'border-slate-200 dark:border-slate-800 hover:border-blue-400 dark:hover:border-blue-700 bg-slate-50/50 dark:bg-slate-950/20'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) handleUploadFiles(Array.from(e.target.files));
              }}
            />
            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
              {isUploading ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <UploadCloud className="w-5 h-5" />
              )}
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                {isUploading
                  ? 'Fájlok feltöltése folyamatban...'
                  : 'Húzza ide a feltöltendő fájlokat, vagy kattintson a tallózáshoz'}
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                PDF, DOCX, XLSX, képek · Több fájl is kijelölhető egyszerre
              </p>
            </div>
          </div>

          {onImportAsContent && (
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400 -mt-2">
              <input ref={contentInputRef} type="file" accept=".pdf,.docx,.txt,.html,.htm" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onImportAsContent({ kind: 'new', file: f }); }} />
              <span>vagy</span>
              <button type="button" onClick={() => contentInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 font-semibold hover:bg-indigo-100 dark:hover:bg-indigo-900/50 cursor-pointer">
                <FileInput className="w-3.5 h-3.5" /> Fájl betöltése a pont tartalmaként…
              </button>
              <span>(PDF, DOCX, TXT, HTML – a szövege a pont leírása lesz, előnézettel)</span>
            </div>
          )}

          {/* Csatolt fájlok listája */}
          <div className="space-y-2">
            <h4 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
              Csatolt dokumentumok ({attachments.length})
            </h4>

            {loading ? (
              <div className="py-8 text-center text-slate-400 flex flex-col items-center justify-center gap-2">
                <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
                <span className="text-xs">Mellékletek betöltése...</span>
              </div>
            ) : attachments.length === 0 ? (
              <div className="py-8 text-center text-slate-400 bg-slate-50 dark:bg-slate-950/30 rounded-xl border border-dashed border-slate-200 dark:border-slate-800 text-xs">
                <Paperclip className="w-6 h-6 mx-auto mb-1.5 text-slate-300 dark:text-slate-600" />
                Ehhez a ponthoz még nem tartozik csatolt fájl.
              </div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {attachments.map((att) => {
                  const isRenamingThis = renamingId === att.id;
                  const isAssigningThis = assigningId === att.id;
                  const token = ApiClient.getToken();
                  const rawUrl = att.r2_key
                    ? `/api/files/${encodeURIComponent(att.r2_key)}`
                    : `/api/public/${tenantSlug}/attachments/${att.id}`;
                  const downloadUrl = token && rawUrl.startsWith('/api/files/')
                    ? `${rawUrl}?token=${encodeURIComponent(token)}`
                    : rawUrl;

                  const fileExt = (att.filename.split('.').pop() || '').toLowerCase();
                  const isHtmlSuspicious = att.mime_type?.includes('text/html') || ['html', 'htm'].includes(fileExt);

                  return (
                    <div
                      key={att.id}
                      className={`border rounded-xl p-3 shadow-2xs transition space-y-2 ${
                        isHtmlSuspicious
                          ? 'bg-amber-50/40 dark:bg-amber-950/20 border-amber-300/80 dark:border-amber-800/60 hover:border-amber-400'
                          : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-blue-300 dark:hover:border-blue-800/80'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        {/* Bal oldal: Ikon és fájlnév / átnevezés */}
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <div className={`p-2 rounded-lg shrink-0 ${isHtmlSuspicious ? 'bg-amber-100 dark:bg-amber-950/60 text-amber-600' : 'bg-slate-100 dark:bg-slate-800'}`}>
                            {getFileIcon(att.filename, att.mime_type)}
                          </div>

                          {isRenamingThis ? (
                            <div className="flex items-center gap-1.5 flex-1 mr-2">
                              <input
                                type="text"
                                value={newFilename}
                                onChange={(e) => setNewFilename(e.target.value)}
                                className="w-full px-2 py-1 bg-white dark:bg-slate-800 border border-blue-400 rounded-lg text-xs text-slate-900 dark:text-white focus:outline-none"
                                autoFocus
                              />
                              <button
                                onClick={() => handleSaveRename(att.id)}
                                disabled={isRenaming}
                                className="p-1 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/50 rounded cursor-pointer"
                                title="Mentés"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setRenamingId(null)}
                                className="p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded cursor-pointer"
                                title="Mégse"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate" title={att.filename}>
                                  {att.filename}
                                </p>
                                {isHtmlSuspicious && (
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700 shrink-0"
                                    title="Ez a fájl mentett weboldalt / bejelentkező felületet tartalmaz PDF dokumentum helyett!"
                                  >
                                    <AlertTriangle className="w-3 h-3 text-amber-600 dark:text-amber-400 shrink-0" />
                                    Mentett weboldal (HTML)
                                  </span>
                                )}
                              </div>
                              <p className="text-[10px] text-slate-400">
                                {formatFileSize(att.size)} · {att.uploaded_at ? new Date(att.uploaded_at).toLocaleDateString('hu-HU') : 'Ismeretlen dátum'}
                                {att.uploaded_by_name ? ` · ${att.uploaded_by_name}` : ''}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Jobb oldal: Műveleti gombok */}
                        {!isRenamingThis && (
                          <div className="flex items-center gap-1 shrink-0">
                            {onImportAsContent && isImportable(att.filename) && (
                              <button
                                type="button"
                                onClick={() => onImportAsContent({ kind: 'existing', attachment: att })}
                                className="p-1.5 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 rounded-lg transition cursor-pointer"
                                title="Betöltés a pont tartalmaként (a szövege a leírásba kerül)"
                                aria-label={`${att.filename} betöltése a pont tartalmaként`}
                              >
                                <FileInput className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => setPreviewFile({ url: downloadUrl, name: att.filename, mime: att.mime_type, id: att.id })}
                              className="p-1.5 text-slate-500 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/50 rounded-lg transition cursor-pointer"
                              title="Azonnali Előnézet (PDF, kép, táblázat, weboldal)"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <a
                              href={downloadUrl}
                              target="_blank"
                              rel="noreferrer"
                              download={att.filename}
                              className="p-1.5 text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/50 rounded-lg transition"
                              title="Letöltés"
                            >
                              <Download className="w-4 h-4" />
                            </a>
                            <button
                              type="button"
                              onClick={() => handleStartRename(att)}
                              className="p-1.5 text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 rounded-lg transition cursor-pointer"
                              title="Átnevezés"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleTriggerReplace(att.id)}
                              disabled={isReplacing && replacingAttId === att.id}
                              className="p-1.5 text-slate-500 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/50 rounded-lg transition cursor-pointer"
                              title="Fájl cseréje (új verzió feltöltése)"
                            >
                              <RefreshCw className={`w-4 h-4 ${isReplacing && replacingAttId === att.id ? 'animate-spin text-amber-500' : ''}`} />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setAssigningId(isAssigningThis ? null : att.id);
                                setTargetRecordId('');
                              }}
                              className={`p-1.5 rounded-lg transition cursor-pointer ${
                                isAssigningThis
                                  ? 'bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300'
                                  : 'text-slate-500 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/50'
                              }`}
                              title="Hozzárendelés / másolás másik ponthoz"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setArchiveAtt(att)}
                              className="p-1.5 text-slate-500 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/50 rounded-lg transition cursor-pointer"
                              title="Archiválás (a jogszabályi megőrzési idő kijelzésével)"
                              aria-label={`${att.filename} archiválása`}
                            >
                              <Archive className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(att)}
                              className="p-1.5 text-slate-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/50 rounded-lg transition cursor-pointer"
                              title="Törlés"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Másik ponthoz hozzárendelés / áthelyezés lenyíló modul */}
                      {isAssigningThis && (
                        <div className="pt-2.5 mt-2 border-t border-slate-100 dark:border-slate-800 bg-purple-50/50 dark:bg-purple-950/20 p-2.5 rounded-lg space-y-2">
                          <p className="text-[11px] font-bold text-purple-900 dark:text-purple-200">
                            Fájl hozzárendelése másik közzétételi ponthoz
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                            <div className="sm:col-span-6">
                              <select
                                value={targetRecordId}
                                onChange={(e) => setTargetRecordId(e.target.value)}
                                className="w-full px-2 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-white"
                              >
                                <option value="">-- Válasszon cél pontot... --</option>
                                {candidateRecords.map(c => (
                                  <option key={c.id} value={c.id}>
                                    [{c.page_title}] {c.pont} {c.cim}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="sm:col-span-3">
                              <select
                                value={assignMode}
                                onChange={(e) => setAssignMode(e.target.value as 'copy' | 'move')}
                                className="w-full px-2 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-white"
                              >
                                <option value="copy">Másolás (marad itt is)</option>
                                <option value="move">Áthelyezés (itt megszűnik)</option>
                              </select>
                            </div>
                            <div className="sm:col-span-3 flex justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => handleExecuteAssign(att.id)}
                                disabled={!targetRecordId || isAssigning}
                                className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-xs font-semibold cursor-pointer shadow-xs transition"
                              >
                                {isAssigning ? '...' : 'Végrehajtás'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setAssigningId(null)}
                                className="px-2 py-1.5 text-xs text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
                              >
                                Mégse
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Lábléc */}
        <div className="px-6 py-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-950/50">
          <span className="text-[11px] text-slate-400">
            A törlések és módosítások azonnal érvénybe lépnek.
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-xl text-xs font-medium transition cursor-pointer"
          >
            Bezárás
          </button>
        </div>
      </div>

      {previewFile && (
        <UniversalFilePreviewModal
          isOpen={Boolean(previewFile)}
          onClose={() => setPreviewFile(null)}
          fileName={previewFile.name}
          fileUrl={previewFile.url}
          mimeType={previewFile.mime}
          onReplace={previewFile.id ? () => handleTriggerReplace(previewFile.id!) : undefined}
        />
      )}
    </div>
  );
};
